import clsx from "clsx";
import React, { useState, useRef, useMemo, useEffect } from "react";

import { FieldLabel, View, Text, Button } from "./base";
import { objectsEqual } from "../utils/parse";

type FilterOperator = "AND" | "OR";
type FilterCondition = "equals" | "gt" | "lt" | "gte" | "lte" | "in";

export type FilterSuggestion = {
  field: string;
  label: string;
  description: string;
  examples: string[];
  operators: FilterCondition[];
};

type ParsedFilter = {
  field: string;
  operator: FilterCondition;
  value: string;
  logic?: FilterOperator;
};

type SessionFilterProps = {
  onChange: (filters: Record<string, Record<string, unknown>>) => void;
  className?: string;
  label?: string;
  placeholder?: string;
  error?: string;
  filterSuggestions?: FilterSuggestion[];
  highlightFields?: string[];
};

const DEFAULT_FILTER_SUGGESTIONS: FilterSuggestion[] = [
  {
    field: "status",
    label: "status",
    description: "Filter by session status",
    examples: ["status = completed", "status IN (completed, in_progress)"],
    operators: ["equals", "in"],
  },
  {
    field: "agentEnvironmentId",
    label: "environment",
    description: "Filter by agent environment name",
    examples: ["environment = prod", "environment IN (prod, dev)"],
    operators: ["equals", "in"],
  },
  {
    field: "direction",
    label: "direction",
    description: "Filter by session direction",
    examples: ["direction = inbound", "direction IN (inbound, outbound)"],
    operators: ["equals", "in"],
  },
  {
    field: "origin",
    label: "origin",
    description: "Filter by session origin",
    examples: ["origin = phone", "origin IN (debugger, phone)"],
    operators: ["equals", "in"],
  },
  {
    field: "publicId",
    label: "session",
    description: "Filter by session ID",
    examples: ["session = 456", "session IN (456, 789)"],
    operators: ["equals", "in"],
  },
  {
    field: "telephonyCallId",
    label: "call",
    description: "Filter by call ID",
    examples: ["call = 456", "call IN (456, 789)"],
    operators: ["equals", "in"],
  },
  {
    field: "createdAt",
    label: "timestamp",
    description: "Filter by creation timestamp",
    examples: ["timestamp >= 2024-01-01", "timestamp <= 2024-12-31"],
    operators: ["equals", "gt", "lt", "gte", "lte"],
  },
];

const LOGIC_OPERATORS: FilterOperator[] = ["AND"];

const escapeRegex = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const getCurrentWord = (text: string, cursorPosition: number): string => {
  if (cursorPosition <= 0) return "";

  const beforeCursor = text.substring(0, cursorPosition);
  const words = beforeCursor.split(/\s+/);
  const lastWord = words[words.length - 1] || "";

  // If cursor is at the end of a word (after a space), return empty string
  if (cursorPosition < text.length && text[cursorPosition] === " ") {
    return "";
  }

  return lastWord;
};

const getOperatorValue = (operator: FilterCondition): string => {
  switch (operator) {
    case "gt":
      return ">";
    case "lt":
      return "<";
    case "gte":
      return ">=";
    case "lte":
      return "<=";
    case "in":
      return "IN";
    case "equals":
    default:
      return "=";
  }
};

const parseOperator = (operatorStr?: string): FilterCondition => {
  if (!operatorStr) return "equals";

  switch (operatorStr.toUpperCase()) {
    case ">":
      return "gt";
    case "<":
      return "lt";
    case ">=":
      return "gte";
    case "<=":
      return "lte";
    case "IN":
      return "in";
    case "=":
    default:
      return "equals";
  }
};

const parseFiltersToApiFormat = (input: string, suggestions: FilterSuggestion[]): Record<string, Record<string, unknown>> => {
  if (!input.trim()) return {};

  const result: Record<string, Record<string, unknown>> = {};

  // Split by AND/OR operators while preserving them
  const parts = input.split(/\s+(AND)\s+/i);

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i].trim();

    // Check if this part is a logic operator
    if (LOGIC_OPERATORS.includes(part.toUpperCase() as FilterOperator)) {
      continue;
    }

    // Parse the filter part
    const filter = parseFilterPart(part, suggestions);
    if (filter) {
      const { field, operator, value } = filter;

      // Convert value to API format
      let filterValue: Record<string, unknown> = { $eq: value };

      if (operator === "in") {
        filterValue = { $in: value.split(",").map((v) => v.trim()) };
      } else if (operator === "gt") {
        filterValue = { $gt: value };
      } else if (operator === "lt") {
        filterValue = { $lt: value };
      } else if (operator === "gte") {
        filterValue = { $gte: value };
      } else if (operator === "lte") {
        filterValue = { $lte: value };
      } else {
        filterValue = { $eq: value };
      }

      result[field] = { ...result[field], ...filterValue };
    }
  }

  return result;
};

const parseFilterPart = (part: string, suggestions: FilterSuggestion[]): ParsedFilter | null => {
  // First check for IN pattern with a more flexible regex
  const inMatch = part.match(/^([\w.]+)\s+IN\s*\(\s*(.+)\s*\)$/i);
  if (inMatch) {
    const [, field, valuesString] = inMatch;
    const values = valuesString.split(",").map((v: string) => v.trim().replace(/['"]/g, ""));
    const suggestion = suggestions.find((s) => s.label === field);

    return {
      field: suggestion ? suggestion.field : field,
      operator: "in",
      value: values.join(","), // Store as comma-separated values
    };
  }

  // Match other SQL-like patterns: "field = 'value'", "field >= 'value'", etc.
  const patterns = [
    // Comparison patterns with quotes: "field >= 'value'", "field <= 'value'", "field = 'value'"
    /^([\w.]+)\s*(>=|<=|=|>|<)\s*['"]([^'"]+)['"]$/i,
    // Comparison patterns without quotes: "field >= value", "field <= value", "field = value"
    /^([\w.]+)\s*(>=|<=|=|>|<)\s*([^'"\s]+)$/i,
  ];

  for (const pattern of patterns) {
    const match = part.match(pattern);
    if (match) {
      const [, field, operatorStr, value1, value2] = match;
      const suggestion = suggestions.find((s) => s.label === field);

      return {
        field: suggestion ? suggestion.field : field,
        operator: parseOperator(operatorStr),
        value: value1 || value2,
      };
    }
  }

  return null;
};

export const SessionFilter = ({
  onChange,
  className,
  label = "Session Filters",
  placeholder = "Type filters like 'status IN (completed, in_progress) AND timestamp >= 2024-01-01'",
  error,
  filterSuggestions = DEFAULT_FILTER_SUGGESTIONS,
  highlightFields,
}: SessionFilterProps) => {
  const [currentValue, setCurrentValue] = useState("");
  const [currentFilters, setCurrentFilters] = useState<Record<string, Record<string, unknown>>>({});
  const [isOpen, setIsOpen] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const [justClickedSuggestion, setJustClickedSuggestion] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const currentTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const resolvedHighlightWords = useMemo(() => {
    const defaultWords = ["status", "environment", "timestamp", "session", "call"];
    return Array.from(new Set([...defaultWords, ...(highlightFields || []), ...filterSuggestions.map((suggestion) => suggestion.label)]));
  }, [filterSuggestions, highlightFields]);

  const fieldHighlightRegex = useMemo(() => {
    if (!resolvedHighlightWords.length) {
      return null;
    }
    const pattern = resolvedHighlightWords.map((word) => escapeRegex(word)).join("|");
    return pattern ? new RegExp(`\\b(${pattern})\\b`, "gi") : null;
  }, [resolvedHighlightWords]);

  // Parse the current input to find suggestions
  const suggestions = useMemo(() => {
    if (!isOpen) return [];

    const currentWord = getCurrentWord(currentValue, cursorPosition);
    const allSuggestions: Array<{ type: "field" | "operator" | "logic"; value: string; label: string; description?: string }> = [];

    // Add field suggestions
    filterSuggestions.forEach((suggestion) => {
      if (suggestion.label.toLowerCase().includes(currentWord.toLowerCase())) {
        allSuggestions.push({
          type: "field",
          value: suggestion.label,
          label: suggestion.label,
          description: suggestion.description,
        });
      }
    });

    // Add operator suggestions if we have a field
    const fieldMatch = currentValue.match(/([\w.]+)\s*$/);
    if (fieldMatch) {
      const field = fieldMatch[1];
      const suggestion = filterSuggestions.find((s) => s.field === field || s.label === field);
      if (suggestion) {
        suggestion.operators.forEach((op) => {
          const operatorValue = getOperatorValue(op);
          if (operatorValue.toLowerCase().includes(currentWord.toLowerCase())) {
            allSuggestions.push({
              type: "operator",
              value: operatorValue,
              label: operatorValue,
              description: `Operator for ${suggestion.label}`,
            });
          }
        });
      }
    }

    // Add logic operator suggestions
    LOGIC_OPERATORS.forEach((op) => {
      if (op.toLowerCase().includes(currentWord.toLowerCase())) {
        allSuggestions.push({
          type: "logic",
          value: op,
          label: op,
          description: "Logical operator",
        });
      }
    });

    return allSuggestions.slice(0, 8); // Limit to 8 suggestions
  }, [currentValue, cursorPosition, isOpen, filterSuggestions]);

  const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const newCursorPosition = e.target.selectionStart || 0;

    setCurrentValue(newValue);
    setCursorPosition(newCursorPosition);
    setIsOpen(true);
    setSelectedSuggestionIndex(0);

    const apiFilters = parseFiltersToApiFormat(newValue, filterSuggestions);
    if (!objectsEqual(currentFilters, apiFilters)) {
      setCurrentFilters(apiFilters);
    }
  };

  const onKeyDownInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || suggestions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedSuggestionIndex((prev) => (prev + 1) % suggestions.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedSuggestionIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
        break;
      case "Enter":
      case "Tab":
        e.preventDefault();
        insertSuggestion(suggestions[selectedSuggestionIndex]);
        break;
      case "Escape":
        setIsOpen(false);
        break;
    }
  };

  const insertSuggestion = (suggestion: { type: string; value: string }) => {
    const currentWord = getCurrentWord(currentValue, cursorPosition);
    const beforeCursor = currentValue.substring(0, cursorPosition - currentWord.length);
    const afterCursor = currentValue.substring(cursorPosition);

    let newValue = beforeCursor + suggestion.value;
    let newCursorPosition = beforeCursor.length + suggestion.value.length;

    // Add appropriate separators for SQL-like syntax
    if (suggestion.type === "field") {
      newValue += " = ";
      newCursorPosition += 3; // " = " is 3 characters
    } else if (suggestion.type === "operator") {
      newValue += " ";
      newCursorPosition += 1; // " " is 1 character
    } else if (suggestion.type === "logic") {
      newValue += " ";
      newCursorPosition += 1; // " " is 1 character
    }

    newValue += afterCursor;

    setCurrentValue(newValue);
    setCursorPosition(newCursorPosition);
    setIsOpen(false);
    setJustClickedSuggestion(true);

    // Notify parent component of the change
    const apiFilters = parseFiltersToApiFormat(newValue, filterSuggestions);
    if (!objectsEqual(currentFilters, apiFilters)) {
      setCurrentFilters(apiFilters);
    }

    // Focus back to input with correct cursor position
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
        inputRef.current.focus();
      }
    }, 0);

    // Reset the flag after a short delay to allow normal focus behavior
    setTimeout(() => {
      setJustClickedSuggestion(false);
    }, 100);
  };

  const onClickSuggestionHandler = (suggestion: { type: string; value: string }) => (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    insertSuggestion(suggestion);
  };

  const onFocusInput = () => {
    if (justClickedSuggestion) {
      setJustClickedSuggestion(false);
      return;
    }
    setIsOpen(true);
  };

  const onSelectInput = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    const newCursorPosition = target.selectionStart || 0;
    setCursorPosition(newCursorPosition);
  };

  const onClickInput = (e: React.MouseEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    const newCursorPosition = target.selectionStart || 0;
    setCursorPosition(newCursorPosition);
  };

  const onBlurInput = (e: React.FocusEvent) => {
    // Don't close if clicking on dropdown
    if (dropdownRef.current && dropdownRef.current.contains(e.relatedTarget as Node)) {
      return;
    }
    setIsOpen(false);
  };

  // Syntax highlighting function that returns React elements
  const highlightSyntax = (text: string) => {
    if (!text) return null;

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;

    // Create regex patterns for different syntax elements (in order of priority)
    const patterns = [
      { regex: /\b(AND|OR|IN)\b/gi, className: "text-blue-600 dark:text-blue-400 font-semibold", priority: 1 },
      { regex: /(>=|<=|=|>|<)/g, className: "text-purple-600 dark:text-purple-400 font-semibold", priority: 2 },
      ...(fieldHighlightRegex ? [{ regex: fieldHighlightRegex, className: "text-green-600 dark:text-green-400 font-medium", priority: 3 }] : []),
      { regex: /(['"][^'"]*['"])/g, className: "text-orange-600 dark:text-orange-400", priority: 4 },
      { regex: /[()]/g, className: "text-gray-500 dark:text-gray-400", priority: 5 },
      { regex: /,/g, className: "text-gray-600 dark:text-gray-300", priority: 6 },
    ];

    // Find all matches and sort by position
    const matches: Array<{ start: number; end: number; className: string; text: string; priority: number }> = [];

    patterns.forEach((pattern) => {
      let match;
      const regex = new RegExp(pattern.regex.source, pattern.regex.flags);
      while ((match = regex.exec(text)) !== null) {
        matches.push({
          start: match.index,
          end: match.index + match[0].length,
          className: pattern.className,
          text: match[0],
          priority: pattern.priority,
        });
      }
    });

    // Add highlighting for unquoted values - simpler approach
    // First, highlight unquoted values after operators
    const operatorValueRegex = /(\w+)\s*(>=|<=|=|>|<)\s+([^'"\s,()]+)/g;
    let operatorMatch;
    while ((operatorMatch = operatorValueRegex.exec(text)) !== null) {
      const valueStart = operatorMatch.index + operatorMatch[0].lastIndexOf(operatorMatch[3]);
      const valueEnd = valueStart + operatorMatch[3].length;

      matches.push({
        start: valueStart,
        end: valueEnd,
        className: "text-orange-600 dark:text-orange-400",
        text: operatorMatch[3],
        priority: 4,
      });
    }

    // Then, highlight unquoted values in IN clauses
    const inValueRegex = /IN\s*\(\s*([^)]+)\s*\)/gi;
    let inMatch;
    while ((inMatch = inValueRegex.exec(text)) !== null) {
      const valuesString = inMatch[1];
      const values = valuesString.split(",").map((v) => v.trim());

      // Find each unquoted value in the IN clause
      let searchStart = inMatch.index + inMatch[0].indexOf(valuesString);
      values.forEach((value) => {
        if (value && !value.match(/^['"]/)) {
          // Only highlight unquoted values
          const valueIndex = valuesString.indexOf(value);
          const valueStart = searchStart + valueIndex;
          const valueEnd = valueStart + value.length;

          matches.push({
            start: valueStart,
            end: valueEnd,
            className: "text-orange-600 dark:text-orange-400",
            text: value,
            priority: 4,
          });
        }
      });
    }

    // Remove overlapping matches (keep higher priority ones)
    const filteredMatches = matches.filter((match, index) => {
      return !matches.some((otherMatch, otherIndex) => {
        if (otherIndex === index) return false;
        if (otherMatch.priority >= match.priority) return false;

        // Check for overlap
        return match.start < otherMatch.end && match.end > otherMatch.start;
      });
    });

    // Sort matches by start position
    filteredMatches.sort((a, b) => a.start - b.start);

    // Build the highlighted parts
    filteredMatches.forEach((match, index) => {
      // Add text before this match
      if (match.start > lastIndex) {
        parts.push(text.slice(lastIndex, match.start));
      }

      // Add the highlighted match
      parts.push(
        <span
          key={index}
          className={match.className}
        >
          {match.text}
        </span>,
      );

      lastIndex = match.end;
    });

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  // Debounce the onChange call
  useEffect(() => {
    if (currentTimeoutRef.current) {
      clearTimeout(currentTimeoutRef.current);
    }

    currentTimeoutRef.current = setTimeout(() => {
      onChange(currentFilters);
    }, 500);
  }, [currentFilters, onChange]);

  return (
    <FieldLabel
      label={label}
      className={clsx("w-full", className)}
      error={error}
    >
      <View
        className="relative w-full mt-1"
        ref={dropdownRef}
      >
        {/* Background container */}
        <View
          className={clsx("absolute inset-0 rounded-lg border transition-colors duration-200", "bg-white dark:bg-gray-800", {
            "border-gray-300 dark:border-gray-600": !error,
            "border-error dark:border-error": error,
          })}
        />

        {/* Syntax highlighted overlay */}
        <View
          className={clsx(
            "absolute inset-0 px-4 py-2 rounded-lg pointer-events-none",
            "text-gray-900 dark:text-gray-100 bg-transparent",
            "overflow-hidden whitespace-nowrap",
          )}
          style={{
            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
            fontSize: "14px",
            lineHeight: "24px",
          }}
        >
          {currentValue ? highlightSyntax(currentValue) : <span className="text-gray-400 dark:text-gray-500">{placeholder}</span>}
        </View>

        {/* Actual input (transparent) */}
        <input
          ref={inputRef}
          type="text"
          className={clsx(
            "relative w-full h-10 px-4 rounded-lg border-0 transition-colors duration-200",
            "text-transparent bg-transparent caret-gray-900 dark:caret-gray-100",
            "placeholder:text-transparent",
            "focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2 dark:focus:ring-offset-gray-900",
            "z-10",
          )}
          style={{
            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
            fontSize: "14px",
            lineHeight: "24px",
          }}
          value={currentValue}
          onChange={onChangeInput}
          onKeyDown={onKeyDownInput}
          onFocus={onFocusInput}
          onBlur={onBlurInput}
          onSelect={onSelectInput}
          onClick={onClickInput}
          placeholder={placeholder}
        />

        {isOpen && suggestions.length > 0 && (
          <View className="absolute top-full -left-1 -right-1 z-50 mt-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {suggestions.map((suggestion, index) => (
              <Button
                key={`${suggestion.type}-${suggestion.value}-${index}`}
                className={clsx("px-4 py-2 cursor-pointer flex items-center justify-between w-full rounded-none", "hover:bg-gray-50 dark:hover:bg-gray-700", {
                  "bg-brand/10 dark:bg-brand/20": index === selectedSuggestionIndex,
                })}
                variant="ghost"
                onClick={onClickSuggestionHandler(suggestion)}
              >
                <View className="flex flex-col items-start">
                  <Text className="text-sm font-medium text-gray-900 dark:text-gray-100">{suggestion.label}</Text>
                  {suggestion.description && <Text className="text-xs font-normal text-gray-500 dark:text-gray-400">{suggestion.description}</Text>}
                </View>
                <View className="flex items-center">
                  <Text className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">{suggestion.type}</Text>
                </View>
              </Button>
            ))}
          </View>
        )}
      </View>
    </FieldLabel>
  );
};
